import { AutocompleteSearchType, CreateTopicRequest, FeatureType, GetCreatorProfileResponse, ImageDisplay, PointCardView, PointOrderingOption, SearchPillSettings, SettingStructure, SettingsChangeEvent } from '../../types';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { StateController } from '../../helpers/state/state-management/state-controller';
import accountInfo from '../../helpers/state/account-info';
import { createTopicRequestFromTopic } from '../../helpers/api/topics-api';
import { detectMobileViewport } from '../../helpers/device';
import featuresInfo from '../../helpers/state/features-info';
import { googleTypesSearchArray } from '../../helpers/constants';
import { isDefined } from '../../helpers/utils';
import pointCache from '../../helpers/state/point-cache';
import topicData from '../../helpers/state/topic-data';

@customElement('admin-advanced-features-fragment')
export class AdminAdvancedFeaturesFragment extends LitElement {
    amplitude_callsite: string = 'Advanced Settings';

    @query('#customMapGeoJson')
    private readonly customMapGeoJsonElement?: HTMLInputElement;

    @property({ type: Boolean })
    show_creator_profiles: boolean = false;

    @property({ type: Boolean })
    isMobile: boolean = false;

    @property({ type: Boolean })
    hasDates?: boolean;

    @property({ type: Boolean })
    shouldFilterPastDates?: boolean;

    @property({ type: Boolean })
    showTabs: boolean = false;

    @state()
    textColor: string = '#000000';

    @state()
    circleColor: string = '#00bcd4';

    @state()
    hidePointNumberingConfigurations: boolean = true;

    @state()
    creatorProfiles: GetCreatorProfileResponse[] = [];

    @state()
    createTopicRequest: CreateTopicRequest = createTopicRequestFromTopic(topicData.topic);

    @state()
    selected_tab: string = 'Point Settings';

    @state()
    settingsListPointName: SettingStructure[] = [];

    @state()
    settingsListPointDisplay: SettingStructure[] = [];

    @state()
    settingsListPointActions: SettingStructure[] = [];

    @state()
    settingsListMapDisplay: SettingStructure[] = [];

    @state()
    settingsListMapActions: SettingStructure[] = [];

    @state()
    settingsListInfoDrawer: SettingStructure[] = [];

    @state()
    needsSave: boolean = false;

    @state()
    needsReload: boolean = false;


    private readonly bindPointCacheState = new StateController(this, pointCache);
    private readonly bindTopicState = new StateController(this, topicData);
    private readonly bindAccountState = new StateController(this, accountInfo);

    async firstUpdated() {
        await this.startup();
    }

    private async startup() {

        this.settingsListPointName = [
            {
                title: `Rename “Point”`,
                inputType: 'inputShort',
                fieldType: 'string',
                fieldName: 'point_name',
                isSubfield: false,
                isHidden: false,
                textCharLimit: 30,
                description: `Don't want to call a point a “point”? Rename it here.`,
                isTopic: true,
                initialValueString: topicData.topic?.point_name,
            }
        ];

        this.settingsListPointDisplay = [
            {
                fieldName: 'enable_point_numbering',
                inputType: 'toggle',
                fieldType: 'boolean',
                subfields: ['replace_icons_with_numbers','point_number_circle_color'],
                title: `Enable ${topicData.topic?.point_name ?? 'Point'} Numbering`,
                initialValueBoolean: topicData.topic?.enable_point_numbering ?? false,
                description:
                    `Enabling this feature lets you assign 3-digit numbers to map ${topicData.topic?.point_name ?? 'point'}s, affecting their order on the map and in the list. You can edit these numbers individually or in bulk using the spreadsheet view.`,
                featureType: FeatureType.PointNumbering,
                hasEntitlement: featuresInfo.pointNumberingEnabled,
                isSubfield: false,
                isHidden: false,
                flippedLanguage: false,
            },
            {
                fieldName: 'replace_icons_with_numbers',
                isSubfield: true,
                subfields: ['point_number_circle_color'],
                isHidden: true,
                title: 'Replace Map Icons with Numbers',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.replace_icons_with_numbers ?? false,
                description:
                    'If checked, Proxi will replace category icons in the map with numbers where assigned.',
                featureType: FeatureType.PointNumbering,
                hasEntitlement: featuresInfo.pointNumberingEnabled,
                flippedLanguage: false,
            },
            {
                fieldName: 'point_number_circle_color',
                isSubfield: true,
                isHidden: true,
                title: `${topicData.topic?.point_name ?? 'Point'} Number Default Color`,
                initialValueString: topicData.topic?.point_number_circle_color ?? '#000000',
                description: 'If you are using the Proxi colors as your category icon, we will use that as the background. Otherwise, we will use the color selected below',
                featureType: FeatureType.PointNumbering,
                hasEntitlement: featuresInfo.pointNumberingEnabled,
                fieldType:'string',
                inputType: 'color',
            },
            {
                fieldName: 'has_dates',
                title: `Enable Dates and Times on ${topicData.topic?.point_name ?? 'Point'}s`,
                inputType: 'toggle',
                fieldType:'boolean',
                subfields: ['should_filter_past_events', 'has_date_only', 'has_time_only', 'has_end_date_time', 'show_easy_date_filters'],
                initialValueBoolean: topicData.topic?.has_dates ?? false,
                description:
                    'Ideal for events and beyond: Checking this box enables you and your collaborators to add specific dates to map points.',
                featureType: FeatureType.DateTime,
                hasEntitlement: featuresInfo.dateTimeEnabled,
                isSubfield: false,
                isHidden: false,
                flippedLanguage: false,
            },
            {
                fieldName: 'has_date_only',
                title: 'Show Date Only',
                flippedLanguage: false,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.has_date_only ?? false,
                description: 'If turned on, users will only enter a date, not a time.',
                featureType: FeatureType.DateTime,
                hasEntitlement: featuresInfo.dateTimeEnabled,
                isSubfield: true,
                isHidden: true,
            },
            {
                fieldName: 'has_time_only',
                title: 'Show Time Only',
                flippedLanguage: false,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.has_time_only ?? false,
                description: 'If turned on, users will only enter a time, not a date.',
                featureType: FeatureType.DateTime,
                hasEntitlement: featuresInfo.dateTimeEnabled,
                isSubfield: true,
                isHidden: true,
            },
            {
                fieldName: 'has_end_date_time',
                title: 'Show End Date and Time',
                flippedLanguage: false,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.has_end_date_time ?? true,
                description: 'If turned off, users will not be able to enter an end date and time, creating a time range',
                featureType: FeatureType.DateTime,
                hasEntitlement: featuresInfo.dateTimeEnabled,
                isSubfield: true,
                isHidden: true,
            },
            {
                fieldName: 'should_filter_past_events',
                title: 'Show Past Events',
                flippedLanguage: true,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.should_filter_past_events ?? false,
                isSubfield: true,
                isHidden: true,
                description: 'If turned off, Proxi will hide points that have passed their end date.',
                featureType: FeatureType.DateTime,
                hasEntitlement: featuresInfo.dateTimeEnabled,
            },
            {
                fieldName: 'show_easy_date_filters',
                title: 'Show Easy Date Filters',
                flippedLanguage: false,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.show_easy_date_filters ?? true,
                isSubfield: true,
                isHidden: true,
                description: 'Keep this on to show time filters like Today, Tomorrow, This Weekned in the Date/Time Filterer',
                featureType: FeatureType.DateTime,
                hasEntitlement: featuresInfo.dateTimeEnabled,
            },
            {
                fieldName: 'point_sort_order',
                title: 'Default Point Sort Order',
                inputType: 'order',
                fieldType:'vector',
                initialValueVector: topicData.topic?.point_sort_order ?? [],
                vectorOptions: [
                    {text: 'Alphabetical', value: PointOrderingOption.Alphabetical},
                    {text: 'Number', value: PointOrderingOption.Number},
                    {text: 'Category', value: PointOrderingOption.Category},
                    {text: 'Distance', value: PointOrderingOption.Distance},
                    {text: 'Chronological', value: PointOrderingOption.Start}
                ],
                description: 'Choose the default order in which points are displayed on the map. Drag and drop to reorder.',
            },
            {
                fieldName: 'disable_google_images',
                title: 'Show Google Images',
                flippedLanguage: true,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_google_images ?? false,
                description:
                    'By default, Proxi pulls in images from Google. You can disable this here.',
                featureType: FeatureType.DisableGoogleImages,
                hasEntitlement: featuresInfo.disableGoogleImagesEnabled,
            },
            {
                fieldName: 'image_display',
                title: 'Image Display',
                inputType: 'dropdown',
                fieldType:'string',
                initialValueString: topicData.topic?.image_display ?? ImageDisplay.Cover,
                dropdownOptions: [
                { value: ImageDisplay.Cover, label: 'Cover' },
                { value: ImageDisplay.Contain, label: 'Contain' },
                { value: ImageDisplay.ContainBlur, label: 'Contain (Blur)' }
                ],
                description:
                    'Decide how you want images to display on your map. Cover will fill the space, while Contain will fit the image inside the space.',
                hasEntitlement: true,
            },
            {
                fieldName: 'hide_featured_on',
                title: `Show 'Also Featured On'`,
                flippedLanguage: true,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_featured_on ?? false,
                description:
                    'By default, Proxi highlights other maps that your points are appearing on. You can turn that off here.',
                featureType: FeatureType.DisableAlsoFeaturedOn,
                hasEntitlement: featuresInfo.disableAlsoFeaturedOnEnabled
            },
            {
                fieldName: 'embed_all_description_links',
                title: 'Embed All Description Links',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.embed_all_description_links ?? false,
                description: 'Turn this on if you want Proxi to attempt to embed all links in the description of your points. E.g. if you add a youtube link, it will embed the video.',
                flippedLanguage: false,
            }
        ];

        this.settingsListPointActions= [
            {
                fieldName: 'has_likes',
                title: 'Enable Upvoting/Liking',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.has_likes ?? false,
                description: 'Selecting this allows map viewers and contributors to "upvote" points. The total "upvotes" appear on the point card for everyone to see.',
                featureType: FeatureType.Upvoting,
                hasEntitlement: featuresInfo.upvotingEnabled,
                flippedLanguage: false,
            },
            {
                fieldName: 'hide_multi_select',
                flippedLanguage: true,
                title: 'Allow Point Multi-Select',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_multi_select ?? false,
                description: 'This allows users to select multiple points at once.',
                featureType: FeatureType.HideMultiSelect,
                hasEntitlement: featuresInfo.hideMultiSelectEnabled,
                subfields: ['hide_get_route'],
            },
            {
                fieldName: 'hide_get_route',
                flippedLanguage: true,
                title: 'Show Get Route Button',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_get_route ?? true,
                featureType: FeatureType.HideGetRoute,
                hasEntitlement: featuresInfo.hideGetRouteEnabled,
                isSubfield: true,
            },
            {
                fieldName: 'open_links_in_same_tab',
                title: 'Open Links in Same Tab',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.open_links_in_same_tab ?? false,
                description: 'By default, Proxi opens links in a new tab. You can change that here.',
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled,
                flippedLanguage: false,
            },
            {
                fieldName: 'disable_get_directions',
                flippedLanguage: true,
                title: 'Show Directions Button',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_get_directions,
                subfields: ['text_get_directions' , 'type_get_directions'],
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'text_get_directions',
                isSubfield: true,
                isHidden: true,
                title: 'Custom Directions Text',
                fieldType:'string',
                initialValueString: topicData.topic?.text_get_directions,
                description: `Don’t want to say “Get Directions”? Rename it here.`,
                textCharLimit: 30,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled,
                inputType: 'inputShort',
            },
            {
                fieldName: 'type_get_directions',
                isSubfield: true,
                isHidden: true,
                title: 'Default Directions Type',
                fieldType:'string',
                initialValueString: topicData.topic?.type_get_directions ?? 'drive',
                description: `Select the default directions type.`,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled,
                inputType: 'dropdown',
                dropdownOptions: [
                    { value: 'drive', label: 'Drive' },
                    { value: 'walk', label: 'Walk' },
                    { value: 'bike', label: 'Bike' },
                    { value: 'transit', label: 'Transit' }
                ],
            },
            {
                fieldName: 'disable_mark_as_visited',
                flippedLanguage: true,
                title: 'Show Mark as Visited Button',
                description: 'This must be enabled if you are running a challenge or passport on your map.',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_mark_as_visited,
                subfields: ['text_mark_as_visited', 'text_visited'],
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'text_mark_as_visited',
                isSubfield: true,
                isHidden: true,
                title: 'Custom Mark as Visited Text',
                fieldType:'string',
                initialValueString: topicData.topic?.text_mark_as_visited,
                description: `Don’t want to say “Mark as Visited”? Rename it here.`,
                textCharLimit: 30,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled,
                inputType: 'inputShort',
            },
            {
                fieldName: 'text_visited',
                isSubfield: true,
                isHidden: true,
                title: 'Custom Visited Text',
                fieldType:'string',
                initialValueString: topicData.topic?.text_visited,
                description: `Don’t want to say “Visited”? Rename it here.`,
                textCharLimit: 30,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled,
                inputType: 'inputShort',
            },
            {
                fieldName: 'disable_more_info',
                flippedLanguage: true,
                title: 'Show More Info Button',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_more_info,
                subfields: ['text_more_info'],
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'text_more_info',
                isSubfield: true,
                isHidden: true,
                title: 'Custom More Info Text',
                fieldType:'string',
                initialValueString: topicData.topic?.text_more_info,
                description: `Don’t want to say “More Info”? Rename it here.`,
                textCharLimit: 30,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled,
                inputType: 'inputShort',
            },
            {
                fieldName: 'disable_book_experiences',
                flippedLanguage: true,
                title: 'Show OpenTable Button',
                description: 'When a point is a restaurant with OpenTable reservations available, show a “Book a Table” button on the point card.',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_book_experiences,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'disable_add_to_map',
                flippedLanguage: true,
                title: 'Show Add to Map Button',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_add_to_map,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.disableGoogleImagesEnabled,
            }

        ];

        this.settingsListMapDisplay = [
            {
                fieldName: 'show_advanced_filter_row',
                title: 'Auto-open Tag Filters Row',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.show_advanced_filter_row ?? false,
                description: 'If checked, Proxi will automatically show the advanced filter row below the category row upon load.',
                flippedLanguage: false,
            },
            {
                fieldName: 'hide_category_filters',
                title: 'Show Category Filters',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_category_filters ?? false,
                description: 'If turned off, Proxi will hide the category filters on the top of the map and in advanced filters.',
                flippedLanguage: true,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                fieldName: 'hide_tag_filters',
                title: 'Show Tag Filters',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_tag_filters ?? false,
                description: 'If turned off, Proxi will hide the tag filters on the top of the map and in advanced filters.',
                flippedLanguage: true,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                fieldName: 'hide_keyword_filters',
                title: 'Show Keyword Filters',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_keyword_filters ?? false,
                description: 'If turned off, Proxi will hide the keyword filters on the top of the map and in advanced filters.',
                flippedLanguage: true,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                fieldName: 'use_simple_filters_mobile',
                title: 'Use Simple Filters on Mobile',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.use_simple_filters_mobile ?? false,
                description: 'If checked, Proxi will only show the advanced filter button on Mobile -- instead of the usual tags and categories filters at the top of the map. This helps with cluttering when there are a lot of categories and/or tags.',
                flippedLanguage: false,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                fieldName: 'use_simple_filters_desktop',
                title: 'Use Simple Filters on Desktop',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.use_simple_filters_desktop ?? false,
                description: 'If checked, Proxi will only show the advanced filter button on Desktop -- instead of the usual tags and categories filters at the top of the map. This helps with cluttering when there are a lot of categories and/or tags.',
                flippedLanguage: false,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                fieldName: 'text_advanced_filters',
                title: 'Custom Advanced Filters Text',
                inputType: 'inputShort',
                fieldType:'string',
                initialValueString: topicData.topic?.text_advanced_filters ?? 'Advanced Filters',
                description: `Don’t want to say “Advanced Filters”? Rename it here.`,
                textCharLimit: 30,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                subfields: ['pill_text', 'allow_add', 'search_types'],
                fieldName: 'enabled',
                title: 'Enable Search Pill',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.search_pill_settings?.enabled ?? false,
                description: 'If checked, Proxi will show the search pill at the top of the map.',
                flippedLanguage: false,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
            },
            {
                fieldName: 'search_types',
                title: 'Search Types',
                inputType: 'multiSelect',
                fieldType:'vector',
                initialValueVector: topicData.topic?.search_pill_settings?.search_types ?? [AutocompleteSearchType.Point, AutocompleteSearchType.Category, AutocompleteSearchType.Tag],
                vectorOptions: [{ text: AutocompleteSearchType.Point, value: AutocompleteSearchType.Point }, { text: AutocompleteSearchType.Category, value: AutocompleteSearchType.Category }, { text: AutocompleteSearchType.Tag, value: AutocompleteSearchType.Tag }, { text: AutocompleteSearchType.Keyword, value: AutocompleteSearchType.Keyword }, { text: AutocompleteSearchType.GoogleSearch, value: AutocompleteSearchType.GoogleSearch }],
                description: 'Select the types of searches that will be available in the search pill. Place is only available if you have a subscription.',
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
                isSubfield: true,
                subfields: ['search_filters'],
                isHidden: true,
            },
            {
                fieldName: 'search_filters',
                title: 'Limit Google Place Searches to Types of Places',
                inputType: 'multiSelect',
                fieldType:'vector',
                initialValueVector: topicData.topic?.search_pill_settings?.search_filters ?? [],
                vectorOptions: googleTypesSearchArray
                    .filter(type => typeof type.autocomplete_id === 'string' && type.autocomplete_id.length > 0 && isDefined(type.autocomplete_id))
                    .sort((a, b) => (a?.autocomplete_id ?? '').localeCompare(b?.autocomplete_id ?? ''))
                    .map(type => ({ text: type.autocomplete_id ?? '', value: type.autocomplete_id ?? '' })),
                description: 'If you are using the Google Places search, you can limit the types of places that will be returned in the search results. This is useful if you only want to show certain types of places, like postal_codes, restaurants, or parks.',
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
                isSubfield: true,
                isHidden: !(topicData.topic?.search_pill_settings?.enabled ?? true) || !((topicData.topic?.search_pill_settings?.search_types?.includes(AutocompleteSearchType.GoogleSearch)) ?? false),
            },
            {
                fieldName: 'pill_text',
                title: 'Search Pill Text',
                inputType: 'inputShort',
                fieldType:'string',
                initialValueString: topicData.topic?.search_pill_settings?.pill_text ?? undefined,
                description: `Want to add additional text to the initial search pill look? Do it here.`,
                textCharLimit: 30,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
                isSubfield: true,
                isHidden: true,
            },
            {
                fieldName: 'allow_add',
                title: 'Allow Adding Points After Search',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.search_pill_settings?.allow_add ?? true,
                description: 'If checked, Proxi will prompt users to add points after searching for a point if they have add-point access',
                flippedLanguage: false,
                hasEntitlement: featuresInfo.customFilteringOptionsEnabled,
                featureType: FeatureType.CustomFilteringOptions,
                isSubfield: true,
                isHidden: true,
            },

        ];

        this.settingsListMapActions = [
            {
                fieldName: 'show_point_list_on_open',
                title: 'Auto-open Point List',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.show_point_list_on_open ?? false,
                description: 'If checked, Proxi will show the list of points on the left side of the map when it opens.',
                flippedLanguage: false,
            },
            {
                fieldName: 'embed_gesture_greedy',
                title: `Zoom On Scroll When Embedded`,
                flippedLanguage: false,
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.embed_gesture_greedy ?? false,
                description:
                    'Visitors can zoom your embedded map with their scroll wheel. Turn this off if you\'d rather scrolling past the map move the page instead.',
                hasEntitlement: true,
            },

            {
                fieldName: 'hide_bookmark_buttons',
                flippedLanguage: true,
                title: 'Show Bookmark Buttons',
                inputType: 'toggle',
                fieldType:'boolean',
                description: 'Allow map viewers to bookmark your map and associated points. These buttons sometimes appear in the header and in the info drawer.',
                initialValueBoolean: topicData.topic?.hide_bookmark_buttons ?? false,
                featureType: FeatureType.HideBookmarkButtons,
                hasEntitlement: featuresInfo.hideBookmarkEnabled,
            },
            {
                fieldName: 'disable_powered_by_proxi',
                flippedLanguage: true,
                title: `Show 'Built with Proxi' Tag & Earn Credit`,
                description: 'By default, Proxi adds a "Built with Proxi" link at the bottom of the map info drawer. By keeping this on, you will earn $25 in credit for each person who subscribes to Proxi via this link.',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.disable_powered_by_proxi ?? false,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
        ];

        this.settingsListInfoDrawer = [
            {
                fieldName: 'hide_map_info_drawer',
                flippedLanguage: true,
                title: 'Show Map Info Drawer',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_map_info_drawer ?? false,
                description: 'If checked, Proxi will hide the map info drawer on the right side of the map.',
                featureType: FeatureType.CustomizeAboutMapDrawer,
                hasEntitlement: featuresInfo.customizeAboutMapEnabled,
                subfields: ['disable_share_button', 'disable_search_button', 'disable_list_button', 'disable_build_your_own', 'hide_map_stats_in_info_drawer' , 'hide_send_map_to_phone'],
            },
            {
                fieldName: 'disable_share_button',
                flippedLanguage: true,
                title: 'Show Share Buttons',
                inputType: 'toggle',
                fieldType:'boolean',
                isSubfield: true,
                isHidden: false,
                initialValueBoolean: topicData.topic?.disable_share_button ?? false,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'hide_send_map_to_phone',
                flippedLanguage: true,
                title: 'Show Send Map to Phone Button',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_send_map_to_phone ?? true,
                featureType: FeatureType.CustomizeAboutMapDrawer,
                hasEntitlement: featuresInfo.customizeAboutMapEnabled,
                isSubfield: true,
                isHidden: false,
            },
            {
                fieldName: 'disable_search_button',
                flippedLanguage: true,
                title: 'Show Search Field',
                inputType: 'toggle',
                fieldType:'boolean',
                isSubfield: true,
                isHidden: false,
                initialValueBoolean: topicData.topic?.disable_search_button ?? false,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'disable_list_button',
                flippedLanguage: true,
                title: 'Show List of Points',
                inputType: 'toggle',
                fieldType:'boolean',
                isSubfield: true,
                isHidden: false,
                initialValueBoolean: topicData.topic?.disable_list_button ?? false,
                featureType: FeatureType.HideAllCommandCenterButtons,
                hasEntitlement: featuresInfo.hideAllCommandCenterButtonsEnabled
            },
            {
                fieldName: 'disable_build_your_own',
                flippedLanguage: true,
                title: 'Show Build Your Own Map Button',
                inputType: 'toggle',
                fieldType:'boolean',
                isHidden: false,
                isSubfield: true,
                initialValueBoolean: topicData.topic?.disable_build_your_own ?? false,
                featureType: FeatureType.HideBuildYourOwnMap,
                hasEntitlement: featuresInfo.hideBuildYourOwnMapEnabled
            },
            {
                fieldName: 'hide_map_stats_in_info_drawer',
                flippedLanguage: true,
                title: 'Show Map Stats in Info Drawer',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_map_stats_in_info_drawer ?? false,
                featureType: FeatureType.CustomizeAboutMapDrawer,
                hasEntitlement: featuresInfo.customizeAboutMapEnabled,
                isHidden: false,
                isSubfield: true,
            },
                        {
                fieldName: 'hide_print_button',
                description: 'Allow map viewers to print or create pdf of your map and associated points.',
                flippedLanguage: true,
                title: 'Show Print Button',
                inputType: 'toggle',
                fieldType:'boolean',
                initialValueBoolean: topicData.topic?.hide_print_button ?? false,
                featureType: FeatureType.HideBookmarkButtons,
                hasEntitlement: featuresInfo.hideBookmarkEnabled,
            },
            {
                fieldName: 'default_list_view',
                title: 'Default List Display',
                inputType: 'dropdown',
                fieldType:'string',
                initialValueString: topicData.topic?.default_list_view ?? PointCardView.List,
                dropdownOptions: [
                { value: PointCardView.List, label: 'List' },
                { value: PointCardView.Cards, label: 'Grid' }
                ],
                description:
                    'Decide how you want items to be displayed in the list view first. List will show items in a vertical list, while Card will show items in a grid of cards.',
                hasEntitlement: true,
            },
            {
                fieldName: 'description',
                title: 'Map Description',
                inputType: 'inputLong',
                fieldType:'string',
                description: 'Add a description to your map. This will appear in the map info drawer and if your map is public, it will be used for SEO purposes.',
                initialValueString: topicData.topic?.description ?? '',
            }
        ];

        this.isMobile = detectMobileViewport();
    }

    handleChangeTab(e: CustomEvent) {
        const index = e.detail.tabIndex;
        this.selected_tab = index === 0 ? 'Point Settings' : 'Map Settings';
    }

    async hasChange(e: CustomEvent) {
        this.needsSave = true;
        let newUpdatedValue = e.detail.updatedValue;
        let fieldToUpdate = e.detail.field;

        // Check if fieldToUpdate is a valid key
        if (fieldToUpdate === 'enabled' || fieldToUpdate === 'search_types' ||
            fieldToUpdate === 'search_filters' || fieldToUpdate === 'pill_text' ||
            fieldToUpdate === 'allow_add') {

            const searchPillSettings: SearchPillSettings = topicData.topic?.search_pill_settings ?? {
                enabled: false,
                search_types: [AutocompleteSearchType.Point, AutocompleteSearchType.Category, AutocompleteSearchType.Tag],
                search_filters: [],
                pill_text: undefined,
                allow_add: true
            };

            if (fieldToUpdate === 'enabled') {
                searchPillSettings.enabled = newUpdatedValue;
            } else if (fieldToUpdate === 'search_types') {
                searchPillSettings.search_types = newUpdatedValue;
            } else if (fieldToUpdate === 'search_filters') {
                searchPillSettings.search_filters = newUpdatedValue;
            } else if (fieldToUpdate === 'pill_text') {
                searchPillSettings.pill_text = newUpdatedValue;
            } else if (fieldToUpdate === 'allow_add') {
                searchPillSettings.allow_add = newUpdatedValue;
            }

            fieldToUpdate = 'search_pill_settings';
            newUpdatedValue = searchPillSettings;
        }

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: newUpdatedValue
        }, this.amplitude_callsite, true, true);

        if ( e.detail.field === 'point_sort_order' && topicData.topic !== undefined) {
            // re order
            await pointCache.instantiatePointCache(topicData.topic, topicData.topicKey, true);
        }

        this.requestUpdate();
        topicData.mapComponent?.requestUpdate();
        await topicData.mapComponent?.pointCardPaginationEl?.firstUpdated();

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }
    }


    async onURLChange(e: CustomEvent<SettingsChangeEvent>) {
        const newUpdatedValue = e.detail.updatedValue;
        const fieldToUpdate = e.detail.field;

        if (isDefined(e.detail.callback)) {
            await e.detail.callback();
        }

        await topicData.fetchAndUpdateTopicData({
            [fieldToUpdate]: newUpdatedValue
        }, this.amplitude_callsite);
    }

    openUpgradeDrawer(e: CustomEvent) {
        this.dispatchEvent(
            new CustomEvent('open-upgrade-dialog', {
                detail: {
                    featureType: e.detail.featureType
                },
            })
        );
    }

    private toggleSubfield(e: CustomEvent, settingList: SettingStructure[]) {
        // find title in settingsList as either fieldName or fieldname textand set isHidden to false if it is true and true if it is false
        const fieldTitle = e.detail.fieldTitle;
        const setting = settingList.find(setting => setting.fieldName === fieldTitle);

        if (isDefined(setting)) {
            if (e.detail.hidden === false) {
                setting.isHidden = false;
            } else {
                setting.isHidden = !(setting.isHidden ?? false);
            }
        }
        // update the new isHidden value in this.settingsList
        settingList = [...settingList];
    }

    protected render() {
        return html`
            <div class="advanced-settings__holder">
                <div id="${this.isMobile ? '' : 'container'}">
                    ${this.showTabs
                        ? html`
                            <tabs-component .options=${['Point Settings', 'Map Settings']} @on-select-tab=${this.handleChangeTab}></tabs-component>
                        `: ''}
                    <div class="tab__content">
                        ${this.selected_tab === 'Point Settings'
                            ? html`
                        ${this.settingsListPointName.map((setting) => {
                            return html`
                                <setting-component
                                    style="width:${setting.widthPercentage ?? 100}%"
                                    .setting_details=${setting}
                                    .is_hidden=${setting.isHidden ?? false}
                                    @on-change-value=${this.hasChange}
                                    @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.settingsListPointDisplay)}
                                    @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                                </setting-component>
                            `;
                        })}

                        <hr class="divider" />
                        <h4 class="sub-title">${topicData.topic?.point_name ?? 'Point'} Display</h4>

                        ${this.settingsListPointDisplay.map((setting) => {
                            return html`
                                <setting-component
                                    style="width:${setting.widthPercentage ?? 100}%"
                                    .setting_details=${setting}
                                    .is_hidden=${setting.isHidden ?? false}
                                    @on-change-value=${this.hasChange}
                                    @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.settingsListPointDisplay)}
                                    @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                                </setting-component>
                            `;
                        })}

                        <hr class="divider" />

                        <h4 class="sub-title">${topicData.topic?.point_name ?? 'Point'} Actions</h4>

                        ${this.settingsListPointActions.map((setting) => {
                            return html `
                            <setting-component
                                style="width:${setting.widthPercentage ?? 100}%"
                                .setting_details=${setting}
                                .is_hidden=${setting.isHidden ?? false}
                                @on-change-value=${this.hasChange}
                                @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.settingsListPointActions)}
                                @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                            </setting-component>`;
                        })}`

                    : html`
                        <h4 class="sub-title">Display</h4>

                        <span class="info-paragraph">
                            Customize how people can interact with your map.
                        </span>

                        ${this.settingsListMapActions.map((setting) => {
                            return html `
                            <setting-component
                                style="width:${setting.widthPercentage ?? 100}%"
                                .setting_details=${setting}
                                @on-change-value=${this.hasChange}
                                @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.settingsListMapActions)}
                                @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                            </setting-component>`;
                        })}

                        <hr class="divider" />

                        <h4 class="sub-title">Filters</h4>
                        <span class="info-paragraph">
                            Customize how filters and tags are displayed on your map.
                        </span>

                        ${this.settingsListMapDisplay.map((setting) => {
                            return html `
                            <setting-component
                                style="width:${setting.widthPercentage ?? 100}%"
                                .setting_details=${setting}
                                .is_hidden=${setting.isHidden ?? false}
                                @on-change-value=${this.hasChange}
                                @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.settingsListMapDisplay)}
                                @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                            </setting-component>`;
                        })}

                        <hr class="divider" />

                        <h4 class="sub-title">Map Info Drawer</h4>
                        <span class="info-paragraph">
                            Customize how the map info drawer looks and what it contains.
                        </span>

                        ${this.settingsListInfoDrawer.map((setting) => {
                            return html `
                            <setting-component
                                style="width:${setting.widthPercentage ?? 100}%"
                                .setting_details=${setting}
                                .is_hidden=${setting.isHidden ?? false}
                                @on-change-value=${this.hasChange}
                                @on-toggle-subfield=${(e: CustomEvent) => this.toggleSubfield(e, this.settingsListMapDisplay)}
                                @open-upgrade-dialog=${(e: CustomEvent) => this.openUpgradeDrawer(e)}
                                >
                            </setting-component>`;
                        })}

                        <hr class="divider" />

                        ${topicData.topic?.custom_map_geojson && topicData.topic?.custom_map_geojson.length > 0 ? html`
                            <h4 class="sub-title">Custom Shapes</h4>
                            <p> DEPRECATED: Please use the new DRAW feature to add shapes.
                            </p>

                            <mwc-textarea
                                id="customMapGeoJson"
                                class="input"
                                label="Custom Map Shapes GeoJSON"
                                value="${topicData.topic?.custom_map_geojson ?? ''}"
                                charcounter
                                maxlength="500000"
                            ></mwc-textarea>
                        ` : ''}
                    `}
                    </div>
                <error-toast id="errorToast"></error-toast>
                </div>
            </div>
            `;
    }

    static get styles() {
        return css`
            #container {
                padding-right: 20px;
                font-size: 0.9rem;
                display: flex;
                justify-content: flex-start;
                height: 100%;
                display: flex;
                flex-direction: column;
            }

            .tab__content{
                overflow: hidden auto;
                flex: 1;
                padding-right: 10px;
            }

            /* width */
            .tab__content::-webkit-scrollbar {
                width: 5px;
            }

            /* Track */
            .tab__content::-webkit-scrollbar-track {
                background: var(--theme-light-scrollbar-color);
            }

            /* Handle */
            .tab__content::-webkit-scrollbar-thumb {
                background: var(--main-moon-color);
            }

            .title {
                color: var(--main-primary-color);
                text-align: center;
                font-size: 1rem;
                font-weight: 700;
                line-height: 130%;
                margin: 5px 0;
                text-align: left;
            }

            .info-paragraph {
                font-size: 0.8rem;
                color: var(--theme-input-color);
                margin-top: 0;
                margin-bottom: 0;
            }

            .sub-title {
                margin-bottom: 5px;
                margin-top: 5px;
                color: var(--theme-input-color);
            }

            .save-actions {
                display: flex;
                justify-content: flex-end;
                margin: 0.5rem;
            }

            .advanced-settings__holder {
                display: flex;
                flex-direction: column;
                height: 100%;
            }

            .no-save {
                --mdc-theme-primary : var(--main-moon-color);
            }

            .input {
                padding: auto 5px;
                display: block;
                width: 100%
            }

            .divider {
                margin-top: 5px;
                border: 0;
                width: 100%;
                border-top: 1px solid var(--main-primary-color);
            }

            .advanced-settings__tabs {
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 20px;
                border: 1px solid var(--main-primary-color);
                font-size: 12px;
                font-size: 0.9rem;
                --mdc-icon-size: 12px;
            }

            .advanced-settings__tab {
                flex: 1;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                line-height: 150%;
                color: var(--main-primary-color);
                background: transparent;
                padding: 5px 10px;
                cursor: pointer;
            }

            .info-paragraph {
                margin-bottom: 0.5rem;
                color: var(--theme-input-color);
            }

            .advanced-settings__tab.right {
                border-radius: 0px 20px 20px 0px;
            }

            .advanced-settings__tab.left {
                border-radius: 20px 0px 0px 20px;
            }

            .advanced-settings__tab.active {
                background: var(--main-primary-color);
                color: var(--theme-white-color);
            }

            .tab-icon {
                width: 12px;
                height: 12px;
                margin-right: 5px;
            }

            .tab-icon.small {
                width: 12px;
                height: 12px;
            }

            .tab__content {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                justify-content: flex-start;
            }

            .save {
                --mdc-theme-primary: var(--main-success-color);
            }

            .helpSubHeader {
                margin-bottom: 10px;
                font-size: 0.75rem;
                color: var(--main-primary-color);
            }

            .helpHeader {
                color: var(--main-primary-color);
            }

            .colorPicker {
                opacity: 0;
                height: 0;
                width: 0;
                margin: 0;
            }

            .helpSubHeader__gated {
                margin-bottom: 0px;
                color: var(--theme-input-color);
            }

            .edit__icons {
                display: flex;
                align-items: center;
            }

            .edit__icon {
                display: flex;
                margin: 0 10px;
            }

            .edit__icon-image {
                padding-right: 4px;
                --mdc-icon-size: 18px;
                --mdc-icon-button-size: 18px;
            }

            .edit__icon-image-main {
                padding-right: 4px;
                margin-left: auto;
                margin-right: auto;
                height: 32px;
                width: 32px;
            }

            .point-numbering--disabled {
                opacity: 0.5;
                touch-action: none;
                pointer-events: none;
            }

            .gatedField {
                display: flex;
                margin-top: 1rem;
            }

            .gatedSection {
                cursor: pointer;
            }

            .menuPng {
                margin-right: 0.25rem;
                height: 20px;
                margin-top: auto;
            }

            @media only screen and (max-width: 550px) {
                .helpHeader {
                    margin: 0.2em 0;
                }

                .menuPng {
                    margin-top: 20px;
                }
            }
        `;
    }
}
